import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
} from 'react-router-dom'
import { ThemeProvider, CssBaseline, Box } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Provider } from 'react-redux'
import { store } from './store'
import theme from './theme'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import { SnackbarProvider } from './components/SnackbarProvider'
import LoginPage from './pages/auth/LoginPage'
import HomePage from './pages/home/HomePage'
import ProfilePage from './pages/profile/ProfilePage'
import PurchaseContractPage from './pages/purchase/PurchaseContractPage'
import QualityControlPage from './pages/quality/QualityControlPage'
import SaleContractPage from './pages/saleContract/SaleContractPage'
import SaleControlPage from './pages/saleControl/SaleControlPage'
import VirtualWarehousePage from './pages/virtualWarehouse/VirtualWarehousePage'
import ProcurementPage from './pages/procurement/ProcurementPage'
import FuturesControlPage from './pages/futures/FuturesControlPage'
import FactoryPage from './pages/factory/FactoryPage'
import VirtualWarehouseV2Page from './pages/virtualWarehouseV2/VirtualWarehouseV2Page'
import FactorySummaryOverviewPage from './pages/factory/FactorySummaryOverviewPage'
import MatchingContractOverviewPage from './pages/matching/MatchingContractOverviewPage'
import ComingSoonPage from './pages/common/ComingSoonPage'
import { isBuilt } from './config/pageBuildStatus'
import PnLDashboardPage from './pages/dashboard/pnl/PnLDashboardPage'
import LongShortDashboardPage from './pages/dashboard/longShort/LongShortDashboardPage'
import InventoryDashboardPage from './pages/dashboard/inventory/InventoryDashboardPage'
import FuturesPositionPage from './pages/dashboard/futuresPosition/FuturesPositionPage'
import RiskManagementPage from './pages/dashboard/riskManagement/RiskManagementPage'
import CashflowsDashboardPage from './pages/dashboard/cashflows/CashflowsDashboardPage'
import ReportValuePage from './pages/report/ReportValuePage'
import ReportsPage from './pages/report/ReportsPage'
import ReportIndexRedirect, { ReportInputsIndexRedirect } from './pages/report/ReportIndexRedirect'
import MasterDataPage from './pages/masterData/MasterDataPage'
import SettingsPage from './pages/settings/SettingsPage'

// Purchase module pages (synced)

// Vendors + Refs
import RefsHubPage from './pages/refs/RefsHubPage'
import BanksPage from './pages/refs/BanksPage'
import CalendarsPage from './pages/refs/CalendarsPage'
import CertificationsPage from './pages/refs/CertificationsPage'
import DeliveryPlacesPage from './pages/refs/DeliveryPlacesPage'
import DeliveryTermsPage from './pages/refs/DeliveryTermsPage'
import LicensesAdminPage from './pages/refs/LicensesAdminPage'
import PackagesPage from './pages/refs/PackagesPage'
import QualitiesPage from './pages/refs/QualitiesPage'
import AdjustLotsPage from './pages/refs/AdjustLotsPage'
import UpdatesPage from './pages/refs/UpdatesPage'

// Data router (createBrowserRouter) thay cho <BrowserRouter> để dùng được
// `useBlocker` (chặn điều hướng khi có thay đổi chưa lưu — vd BOM report).
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/home" replace />} />
                <Route path="home" element={<HomePage />} />
                <Route path="profile" element={<ProfilePage />} />

                {/* Purchase Contract (legacy single-page) */}
                <Route path="purchase/contract" element={<PurchaseContractPage />} />
                <Route path="purchase/contract/*" element={<PurchaseContractPage />} />

                {/* Purchase module — synced */}

                {/* References */}
                <Route path="purchase/refs" element={<RefsHubPage />} />
                <Route path="purchase/refs/banks" element={<BanksPage />} />
                <Route path="purchase/refs/calendars" element={<CalendarsPage />} />
                <Route path="purchase/refs/certifications" element={<CertificationsPage />} />
                <Route path="purchase/refs/delivery-places" element={<DeliveryPlacesPage />} />
                <Route path="purchase/refs/delivery-terms" element={<DeliveryTermsPage />} />
                <Route path="purchase/refs/licenses" element={<LicensesAdminPage />} />
                <Route path="purchase/refs/packages" element={<PackagesPage />} />
                <Route path="purchase/refs/qualities" element={<QualitiesPage />} />
                <Route path="purchase/refs/adjust-lots" element={<AdjustLotsPage />} />
                <Route path="purchase/refs/updates" element={<UpdatesPage />} />

                {/* Report / Dashboard */}
                {/* Dashboards — parent shell + in-page tab nav (mirror SaleContractPage). */}
                <Route path="report/dashboards" element={<ReportsPage />}>
                  <Route index element={<Navigate to="/report/dashboards/pnl" replace />} />
                  <Route path="pnl" element={null} />
                  <Route path="futures-position" element={null} />
                  <Route path="risk-management" element={null} />
                  <Route path="long-short" element={null} />
                  <Route path="inventory" element={null} />
                  <Route path="cashflows" element={null} />
                </Route>
                {/* Inputs — Navigation container (frmReportMain). */}
                <Route path="report/inputs" element={<ReportInputsIndexRedirect />} />
                <Route path="report/inputs/:tab" element={<ReportValuePage />} />
                {/* /report → section cuối user mở (Dashboards vs Inputs) rồi page tự
                    restore tab bên trong. Xem ReportIndexRedirect (localStorage). */}
                <Route path="report" element={<ReportIndexRedirect />} />

                {/* Sales */}
                <Route path="sales/contract" element={<SaleContractPage />}>
                  {/* Bare-route redirect handled inside SaleContractPage so the
                      user's last-opened tab (preferences registry) wins over a
                      hard-coded /list. */}
                  <Route index element={null} />
                  <Route path="list" element={null} />
                  <Route path="consignment" element={null} />
                  <Route path="amendment" element={null} />
                  <Route path="ptbf-fixation" element={null} />
                  <Route path="references" element={null} />
                </Route>
                <Route path="sales/control" element={<SaleControlPage />} />
                <Route path="sales/control/*" element={<SaleControlPage />} />

                {/* Procurement */}
                <Route path="purchase/control" element={<ProcurementPage />} />
                <Route path="purchase/control/*" element={<ProcurementPage />} />

                {/* Futures Control */}
                <Route path="futures" element={<FuturesControlPage />} />
                <Route path="futures/*" element={<FuturesControlPage />} />

                {/* Factory — URL pattern `/<factoryCode>/...` (KHÔNG còn prefix `/factory/`).
                    Enumerate explicit factory codes để tránh `:slug/*` catch-all bắt nhầm
                    typo URL (vd `/abouttypo`) hay future top-level route. Thêm factory mới
                    → add 2 dòng (literal + wildcard) ở dưới. */}
                <Route path="factory/control" element={<FactorySummaryOverviewPage />} />

                {/* Bảo Lộc */}
                <Route path="baoloc" element={<FactoryPage />} />
                <Route path="baoloc/*" element={<FactoryPage />} />
                {/* Nhơn Trạch */}
                <Route path="nhontrach" element={<FactoryPage />} />
                <Route path="nhontrach/*" element={<FactoryPage />} />
                {/* An Phước (legacy DB code `nguyencuong`) */}
                <Route path="nguyencuong" element={<FactoryPage />} />
                <Route path="nguyencuong/*" element={<FactoryPage />} />
                {/* Nông Sản */}
                <Route path="nongsan" element={<FactoryPage />} />
                <Route path="nongsan/*" element={<FactoryPage />} />
                {/* Pacorini */}
                <Route path="pacorini" element={<FactoryPage />} />
                <Route path="pacorini/*" element={<FactoryPage />} />

                {/* Virtual Warehouse v2 — kho mới kiểu factory (clone FactoryPage),
                    code cố định 'virtualwh'. Data stub trỏ schema Bảo Lộc (Option B).
                    Top-level literal, không đụng /virtual-warehouse cũ.
                    Đang 'to-build' (pageBuildStatus) → gate ComingSoon tới khi mở. */}
                <Route
                  path="virtualwh"
                  element={
                    isBuilt('/virtualwh') ? (
                      <VirtualWarehouseV2Page />
                    ) : (
                      <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                        <ComingSoonPage title="Virtual Warehouse" status="to-build" embedded />
                      </Box>
                    )
                  }
                />
                <Route
                  path="virtualwh/*"
                  element={
                    isBuilt('/virtualwh') ? (
                      <VirtualWarehouseV2Page />
                    ) : (
                      <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                        <ComingSoonPage title="Virtual Warehouse" status="to-build" embedded />
                      </Box>
                    )
                  }
                />

                {/* Matching P - S — mirror Access form `frmMatchingPSMain` */}
                <Route path="matching" element={<MatchingContractOverviewPage />} />
                <Route path="matching/:tab" element={<MatchingContractOverviewPage />} />

                {/* Quality */}
                <Route path="quality" element={<QualityControlPage />} />
                <Route path="quality/*" element={<QualityControlPage />} />

                {/* Virtual Warehouse — frmVirtualFactoryMain: 2 tabs (Received / Quality
                    Discount) resolved from URL by VirtualWarehousePage. DN/RN print are
                    toolbar buttons inside the Received tab. */}
                <Route path="virtual-warehouse" element={<VirtualWarehousePage />} />
                <Route path="virtual-warehouse/*" element={<VirtualWarehousePage />} />


                {/* Master Data — single tabbed page (frmMdMain). MasterDataPage
                    drives top tab + inner tab + "Go to Page" Select from URL. */}
                <Route path="master-data/*" element={<MasterDataPage />} />

                {/* Settings */}
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/*" element={<SettingsPage />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/home" replace />} />
              </Route>
    </>,
  ),
  // future của createBrowserRouter chỉ nhận data-router flags (v7_relativeSplatPath…);
  // v7_startTransition là flag của <RouterProvider> (đặt ở dưới).
  { future: { v7_relativeSplatPath: true } },
)

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
        <SnackbarProvider>
        <Box sx={{ height: '100%', width: '100%', margin: 0, padding: 0 }}>
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </Box>
        </SnackbarProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  )
}

export default App
